import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Alert } from '../components/ui/Alert';
import { Card } from '../components/ui/Card';
import { MockBadge } from '../components/MockBadge';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import { APP_NAME } from '../lib/appName';
import { TOTP_LOCKED_MESSAGE, isTotpLocked } from '../lib/authErrors';
import { ApiError, type AcceptInviteResponse } from '../types/api';

const MIN_PASSWORD_LENGTH = 12;

/** Reads `#token=...` out of the URL fragment, once, on mount. */
function readTokenFromHash(): string | null {
  const hash = window.location.hash;
  if (!hash.startsWith('#')) return null;
  const params = new URLSearchParams(hash.slice(1));
  return params.get('token');
}

/** What the server's answer means for the form: a message, and whether it asks for the existing account's password/code. */
function interpretError(
  err: unknown,
  wasExisting: boolean,
): { message: string; existingAccount?: boolean } {
  if (isTotpLocked(err)) return { message: TOTP_LOCKED_MESSAGE };
  if (!(err instanceof ApiError)) return { message: 'Something went wrong. Try again.' };
  // The route answers `429 invalid_credentials` for a rate-limited password check.
  if (err.status === 429)
    return { message: 'Too many attempts. Wait a few minutes and try again.' };
  switch (err.body.error) {
    case 'invalid_or_expired_token':
      return {
        message:
          'The invite link is invalid, already used, or has expired. Ask an administrator for a new one.',
      };
    case 'password_too_short':
      return { message: `The password must be at least ${MIN_PASSWORD_LENGTH} characters.` };
    case 'password_pwned':
      return {
        message: 'This password has appeared in a previous data breach. Choose a different one.',
      };
    case 'existing_account_auth_required':
      return {
        message:
          'This email already has an account. Log in with your existing password to join this account.',
        existingAccount: true,
      };
    case 'invalid_credentials':
      return {
        message: wasExisting
          ? 'Incorrect password.'
          : 'This email already has an account. Enter its existing password to join this account.',
        existingAccount: true,
      };
    case 'totp_required':
      return {
        message: 'Enter the code from your authenticator app.',
        existingAccount: true,
      };
    case 'invalid_code':
      return { message: 'The code is incorrect or already used.', existingAccount: true };
    default:
      return { message: 'Something went wrong. Try again.' };
  }
}

/**
 * Public page (no `ProtectedShell` — nobody is signed in yet) for accepting
 * an invitation made on the Users page. The token is only ever in the URL
 * fragment (never a query string, so it never reaches a server log or
 * `Referer` header): read once on mount, then stripped from the visible URL
 * with `history.replaceState` before anything is submitted.
 *
 * A new user chooses a password and gets a session straight away (like
 * login); `refreshMe()` loads it, and `ProtectedShell` then leads a user
 * without 2FA into the 2FA setup. An email that already has an account is
 * told to prove it with its own password (+ code), and is only added to
 * this account (`membership_added`), not signed in.
 */
export function InvitePage() {
  const { refreshMe } = useAuth();
  const navigate = useNavigate();
  // Read in the initializer, not the effect: StrictMode runs effects twice, and
  // the second run would find the fragment already stripped.
  const [token] = useState<string | null>(readTokenFromHash);
  const [password, setPassword] = useState('');
  const [passwordAgain, setPasswordAgain] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [existingAccount, setExistingAccount] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    if (window.location.hash) {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, []);

  const mismatch = !existingAccount && passwordAgain.length > 0 && password !== passwordAgain;
  const canSubmit =
    Boolean(token) &&
    !busy &&
    (existingAccount
      ? password.length > 0
      : password.length >= MIN_PASSWORD_LENGTH && password === passwordAgain);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!token || !canSubmit) return;
    setError(null);
    setBusy(true);
    try {
      const cleanedCode = totpCode.replace(/\s/g, '');
      const result = await apiFetch<AcceptInviteResponse>('/invites/accept', {
        method: 'POST',
        body: {
          token,
          password,
          ...(existingAccount && cleanedCode ? { totpCode: cleanedCode } : {}),
        },
      });
      if ('status' in result && result.status === 'membership_added') {
        setAdded(true);
        return;
      }
      // A new user: the server set the session cookie. Load it (and its
      // CSRF token) the way login does; ProtectedShell shows the 2FA setup
      // for a user without 2FA.
      await refreshMe();
      void navigate('/', { replace: true });
    } catch (err) {
      const { message, existingAccount: askExisting } = interpretError(err, existingAccount);
      setError(message);
      if (askExisting && !existingAccount) {
        // The password typed so far was meant to be a new one; the account's own is needed now.
        setExistingAccount(true);
        setPassword('');
        setPasswordAgain('');
      }
      if (isTotpLocked(err)) setTotpCode('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg p-4">
      <Card className="w-[min(400px,100%)] p-8 flex flex-col gap-5">
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-[9px] bg-accent text-white font-bold flex items-center justify-center"
            aria-hidden="true"
          >
            N
          </div>
          <div>
            <div className="font-semibold">{APP_NAME}</div>
            <div className="text-xs text-muted">Accept invitation</div>
          </div>
          <span className="ml-auto">
            <MockBadge />
          </span>
        </div>

        {error ? <Alert>{error}</Alert> : null}

        {added ? (
          <div className="flex flex-col gap-4">
            <div role="status" className="text-success-text text-sm">
              You have been added to the account.
            </div>
            <Button asChild>
              <Link to="/login">Log in</Link>
            </Button>
          </div>
        ) : !token ? (
          <Alert>The invite link is missing or invalid. Ask an administrator for a new one.</Alert>
        ) : (
          <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="invite-password">
                {existingAccount ? 'Your existing password' : 'Choose a password'}
              </Label>
              <Input
                id="invite-password"
                type="password"
                autoComplete={existingAccount ? 'current-password' : 'new-password'}
                required
                minLength={existingAccount ? undefined : MIN_PASSWORD_LENGTH}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {existingAccount ? null : (
                <div className="text-xs text-muted">At least {MIN_PASSWORD_LENGTH} characters.</div>
              )}
            </div>
            {existingAccount ? (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="invite-totp">Code from the authenticator app</Label>
                <Input
                  id="invite-totp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123 456"
                  className="font-mono text-base tracking-widest"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                />
                <div className="text-xs text-muted">Only if the account has 2FA.</div>
              </div>
            ) : (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="invite-password-again">Confirm password</Label>
                <Input
                  id="invite-password-again"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                  value={passwordAgain}
                  onChange={(e) => setPasswordAgain(e.target.value)}
                  aria-invalid={mismatch}
                />
                <div className="min-h-[18px] text-xs text-danger-text" aria-live="polite">
                  {mismatch ? 'The passwords do not match.' : ''}
                </div>
              </div>
            )}
            <Button type="submit" disabled={!canSubmit} className="mt-2">
              {busy ? 'Please wait…' : existingAccount ? 'Join account' : 'Create account'}
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}
